import { useMemo, useState } from 'react';

import { ArrowBackIosNew, Refresh } from '@mui/icons-material';
import { Box, Button, Stack, useTheme } from '@mui/material';
import { Formik } from 'formik';
import * as Yup from 'yup';

import { back, navigate, routes, useParams } from '@redwoodjs/router';
import { Metadata, useMutation, useQuery } from '@redwoodjs/web';

import { NPIAutoComplete } from 'src/components/FormikComponents';
import { FieldInput } from 'src/components/FormikComponents/FieldInput';
import Loader from 'src/components/Loader/Loader';
import { ModalBox } from 'src/components/ModalContent/Modal';
import ReusableGrid from 'src/components/ReusableGrid';
import { TextAreaInput } from 'src/components/TextAreaInput';
import { showErrorToast, showSuccessToast } from 'src/components/Toasts/toast';
import ViewPDFModal from 'src/components/ViewPDFModal';
import { UPDATE_SERVICE_DEFER_RENEWAL } from 'src/graphql/mutations/updateServiceDeferRenewal.mutation';
import { GET_AUDIT_LOGS } from 'src/graphql/queries/auditLogs.query';
import useDeferRenewalMonths from 'src/hooks/useDeferRenewalMonths';
import useMerchantApplication from 'src/hooks/useMerchantApplication';
import { PageContent } from 'src/layouts/MainLayout/PageContent';
import { PageHeaderVariant } from 'src/layouts/MainLayout/PageHeader';
import ServicePromoStatus from 'src/pages/ViewServicePage/components/ServicePromoStatus';
import ServiceTrialStatus from 'src/pages/ViewServicePage/components/ServiceTrialStatus';
import convertTimestampToMonths, { formatDateToGB } from 'src/utils/viewServices';

import NoteSection from '../ViewCheckInCallPage/components/NotesSection';
import ServiceDetailsSection from '../ViewCheckInCallPage/components/ServiceDetailsSection';
import History from '../ViewMerchantPage/components/History/History';

import CheckinCallHistory from './components/CheckinCallHistory';
import InformationCards from './components/InformationCards';
import OutletSection from './components/OutletSection';

const FIELD_INPUT_BOX_STYLING = {
    style: {
        marginTop: '24px',
        width: '80%',
    },
};

const CONTRACT_EXPIRY_THRESHOLDS = {
    CRITICAL_DAYS: 30,
    WARNING_DAYS: 90,
    SAFE_DAYS: 90,
} as const;

const ViewServicePage = () => {
    const { id, from } = useParams();
    const theme = useTheme();
    const [isOpen, setIsOpen] = useState(false);
    const [isDeferModalOpen, setIsDeferModalOpen] = useState(false);
    const { deferRenewalMonths } = useDeferRenewalMonths();
    const [auditLogs, setAuditLogs] = useState([]);

    const [updateServiceDeferRenewal, { loading: deferLoading }] = useMutation(
        UPDATE_SERVICE_DEFER_RENEWAL
    );

    const isFromCustomerSuccess = from === 'customer-success';

    const { data, loading } = useMerchantApplication(Number(id));
    const [selectedCheckInCallNote, setSelectedCheckInCallNote] = useState(null);

    useQuery(GET_AUDIT_LOGS, {
        skip: !data?.id,
        fetchPolicy: 'cache-and-network',
        variables: { entityId: Number(data?.id), entity: 'SERVICE' },
        onCompleted: (queryData) => {
            let completedSeen = false;

            const filteredLogs = queryData.auditLogs?.filter((log) => {
                if (log.details === 'Application completed.') {
                    if (completedSeen) return false;
                    completedSeen = true;
                }
                return true;
            });
            setAuditLogs(filteredLogs);
        },
    });

    const [isPDFModalOpen, setIsPDFModalOpen] = useState(false);

    const PANEL_HEIGHT = 600;

    const onOpenClosePDFModal = () => {
        setIsPDFModalOpen(!isPDFModalOpen);
    };

    const serviceExpiryInMonths = useMemo(() => {
        if (loading || !data?.service?.expiryDate) return null;
        return convertTimestampToMonths(new Date(data.service.expiryDate).getTime());
    }, [loading, data?.service?.expiryDate]);

    const isContractExpiring = useMemo(() => {
        if (!serviceExpiryInMonths?.months) return false;
        const contractExpiry = parseInt(serviceExpiryInMonths.months, 10);
        return contractExpiry <= 3;
    }, [serviceExpiryInMonths?.months]);

    if (loading || !data) return <Loader />;

    const valueStyle = {
        textDecoration: 'underline',
        color: theme.palette.text.teal,
    };

    const getContractExpiryColor = () => {
        if (!serviceExpiryInMonths?.months && !serviceExpiryInMonths?.days) {
            return theme.palette.grey[600];
        }

        const monthsRemaining = parseInt(serviceExpiryInMonths.months || '0', 10);
        const daysRemaining = parseInt(serviceExpiryInMonths.days || '0', 10);

        const totalDaysRemaining = Math.round(monthsRemaining * 30.44) + daysRemaining;

        if (totalDaysRemaining < 0) {
            return theme.palette.error.main;
        }

        if (totalDaysRemaining <= CONTRACT_EXPIRY_THRESHOLDS.CRITICAL_DAYS) {
            return theme.palette.error.main; // Red
        }

        if (totalDaysRemaining <= CONTRACT_EXPIRY_THRESHOLDS.WARNING_DAYS) {
            return theme.palette.orange.main;
        }

        return theme.palette.success.main;
    };

    const contractExpiryStyle = {
        color: getContractExpiryColor(),
        fontWeight: '600',
    };

    const verticalInfoCards = [
        {
            title: 'Merchant Name',
            value: data?.tradingName ?? data?.merchantInformation?.aboutBusiness?.tradingName,
            valueStyle,
            enableNavigation: true,
            navigateTo: routes.viewMerchant({
                merchantAccountNumber: data?.merchantAccountNumber,
            }),
        },
        {
            title: 'Merchant Account Number',
            value: data?.merchantAccountNumber,
        },
        {
            title: 'MID',
            value: data?.mid,
            titleSide: 'left',
        },
        {
            title: 'Partner Name',
            value: `${data?.partner?.firstName} ${data?.partner?.lastName}`,
            valueStyle,
            enableNavigation: true,
            navigateTo: routes.viewPartner({
                id: data?.partnerId,
                partnerOfPartner: false,
            }),
        },
    ];

    const horizontalInfoCards = [
        {
            title: 'Contract Term',
            value: `${data?.serviceRequirements?.service?.contractTerm?.replace('_', ' ')}`,
        },
        {
            title: 'Contract Expiry Date',
            value: formatDateToGB(data?.service?.expiryDate),
            subtitle: `${serviceExpiryInMonths?.months} Months, ${serviceExpiryInMonths?.days} days`,
            valueStyle: contractExpiryStyle,
        },
        {
            title: 'Merchant Agreement',
            value: 'View Agreement',
            valueStyle: {
                textDecoration: 'underline',
                color: !data?.agreementDocumentUrl
                    ? theme.palette.text.disabled
                    : theme.palette.text.teal,
            },
            enableNavigation: false,
            disableTrigger: !data?.agreementDocumentUrl,
            onClickFunction: onOpenClosePDFModal,
        },
    ];

    const displayConfig = {
        showMerchantAgreement: false,
        showAddServiceButton: false,
    };

    const serviceId = Number.parseInt(id, 10);

    return (
        <PageContent
            headerVariant={PageHeaderVariant.DASHBOARD}
            heading={'Service Dashboard'}
            subHeading={data?.service?.serviceNumber || id}
            callToActions={
                <Stack
                    width="100%"
                    direction="row"
                    spacing={2}
                    justifyContent={'end'}
                    alignItems={'end'}
                >
                    {isFromCustomerSuccess && (
                        <Button
                            variant="npi-teal"
                            onClick={() => {
                                return setIsDeferModalOpen(true);
                            }}
                        >
                            Defer Renewal
                        </Button>
                    )}

                    {isContractExpiring && (
                        <Button startIcon={<Refresh />} variant="npi-success" onClick={() => {}}>
                            Renew
                        </Button>
                    )}

                    <Button startIcon={<ArrowBackIosNew />} variant="npi-primary" onClick={back}>
                        Back
                    </Button>
                </Stack>
            }
        >
            <Metadata title="Service Dashboard" description="Service Dashboard Page" />

            <Stack gap={2}>
                <ServiceTrialStatus serviceId={serviceId} />
                <ServicePromoStatus serviceId={serviceId} />
            </Stack>

            <Box display="flex" gap={2} mt={2}>
                <OutletSection height={PANEL_HEIGHT} outlets={data?.merchantInformation?.outlets} />
                <InformationCards items={verticalInfoCards} height={PANEL_HEIGHT} />
            </Box>

            <Box display="flex" gap={2} mt={2}>
                <InformationCards isHorizontal items={horizontalInfoCards} />
            </Box>

            <ServiceDetailsSection
                contractUrl={data?.agreementDocumentUrl}
                openPDFModal={onOpenClosePDFModal}
                terminalId={data?.terminalId}
                orderPrimary={data?.serviceRequirements?.productOrder?.primaryProducts}
                orderSecondary={data?.serviceRequirements?.productOrder?.secondaryProducts}
                orderPrimaryUrls={data?.serviceRequirements?.order?.primary}
                orderSecondaryUrls={data?.serviceRequirements?.order?.secondary}
                merchantId={data?.merchant?.id}
                displayConfig={displayConfig}
            />

            <Box display="flex" gap={2} mt={2}>
                <CheckinCallHistory
                    serviceId={Number.parseInt(id, 10)}
                    onOpen={(dateTime, subject, addedBy, id, body) => {
                        setIsOpen(true);
                        setSelectedCheckInCallNote({ dateTime, subject, addedBy, id, body });
                    }}
                />
            </Box>

            <Stack direction={'row'} gap={2}>
                <Box sx={{ flex: 1 }}>
                    <NoteSection merchantId={data?.merchant?.id} />
                </Box>

                <Box sx={{ flex: 1 }}>
                    <ReusableGrid title="History" xs={12} enableContainer>
                        <History auditLogs={auditLogs} />
                    </ReusableGrid>
                </Box>
            </Stack>

            <ViewPDFModal
                title="Card Service Agreement PDF"
                fileName="Card Service Agreement"
                file={data?.agreementDocumentBytes}
                fileURL={data?.agreementDocumentUrl}
                isModalOpen={isPDFModalOpen}
                setIsModalOpen={onOpenClosePDFModal}
            />

            <ModalBox
                open={isDeferModalOpen}
                handleCloseModal={() => {
                    setIsDeferModalOpen(false);
                }}
                title="Defer Service Renewal"
                widthSize="740px"
            >
                <Formik
                    initialValues={{ deferMonth: '' }}
                    validationSchema={Yup.object().shape({
                        deferMonth: Yup.mixed().required('Please select a defer renewal period'),
                    })}
                    onSubmit={async (values: { deferMonth: any }) => {
                        const deferMonths =
                            typeof values.deferMonth === 'object'
                                ? values.deferMonth.value
                                : values.deferMonth;

                        try {
                            await updateServiceDeferRenewal({
                                variables: {
                                    input: {
                                        serviceId: Number.parseInt(id, 10),
                                        deferRenewalMonths: Number(deferMonths),
                                        applicationId: data?.id,
                                    },
                                },
                            });
                            setIsDeferModalOpen(false);
                            showSuccessToast('Service renewal deferred successfully');
                            navigate(`/customer-success/expired-services`);
                        } catch (error) {
                            console.error('Error deferring service renewal:', error);
                            return showErrorToast(err.message);
                        }
                    }}
                >
                    {({ handleSubmit, values }) => {
                        return (
                            <Box display="flex" flexDirection="column" margin={1}>
                                <Box sx={{ width: '340px' }}>
                                    <NPIAutoComplete
                                        header="Defer Renewal"
                                        name="deferMonth"
                                        sortBy="value"
                                        options={[...deferRenewalMonths].map(
                                            (obj: { month: number }) => {
                                                const { month } = obj;
                                                return {
                                                    id: month,
                                                    label: `${month} month${month > 1 ? 's' : ''}`,
                                                    value: month,
                                                };
                                            }
                                        )}
                                        placeholder="Select Defer Date"
                                    />
                                </Box>

                                <Box
                                    display={'flex'}
                                    flexDirection={'row'}
                                    justifyContent={'flex-end'}
                                    gap={2}
                                    mt={2}
                                >
                                    <Button
                                        variant="npi-primary"
                                        onClick={() => {
                                            setIsDeferModalOpen(false);
                                        }}
                                        sx={{ width: '140px' }}
                                        disabled={deferLoading}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        variant="npi-primary"
                                        onClick={() => {
                                            handleSubmit();
                                        }}
                                        sx={{ width: '140px' }}
                                        disabled={
                                            deferLoading ||
                                            !values.deferMonth ||
                                            (typeof values.deferMonth === 'object' &&
                                                !values.deferMonth.value)
                                        }
                                    >
                                        {deferLoading ? 'Saving...' : 'Save'}
                                    </Button>
                                </Box>
                            </Box>
                        );
                    }}
                </Formik>
            </ModalBox>

            <ModalBox
                open={isOpen}
                handleCloseModal={() => {
                    setSelectedCheckInCallNote(null);
                    setIsOpen(false);
                }}
                title="Check-in Call"
                widthSize="640px"
            >
                <Formik
                    initialValues={{
                        subject: selectedCheckInCallNote?.subject ?? '',
                        dateTime: selectedCheckInCallNote?.dateTime ?? '',
                        addedBy: selectedCheckInCallNote?.addedBy ?? '',
                        callNotes: selectedCheckInCallNote?.body ?? '',
                    }}
                    validationSchema={Yup.object().shape({})}
                    onSubmit={() => {}}
                >
                    <Box display="flex" flexDirection="column">
                        <FieldInput
                            type="text"
                            name="subject"
                            label={`Subject`}
                            placeholder="Enter subject"
                            disabled
                            boxStyling={FIELD_INPUT_BOX_STYLING}
                        />

                        <FieldInput
                            type="text"
                            name="dateTime"
                            label={`Date/Time`}
                            placeholder="Enter date/time"
                            disabled
                            boxStyling={FIELD_INPUT_BOX_STYLING}
                        />

                        <FieldInput
                            type="text"
                            name="addedBy"
                            label={`Added By`}
                            placeholder="Enter added by"
                            disabled
                            boxStyling={FIELD_INPUT_BOX_STYLING}
                        />
                        <Box display={'flex'} flexDirection={'row'} width={'100%'} marginTop={2}>
                            <Box display="flex" flexDirection="column" flex={1}>
                                <TextAreaInput
                                    disabled
                                    name="callNotes"
                                    label="Call Notes"
                                    placeholder="..."
                                    inputStyle={{ style: { width: '100%' } }}
                                />
                            </Box>
                        </Box>

                        <Box
                            display={'flex'}
                            flexDirection={'row'}
                            justifyContent={'flex-end'}
                            mt={2}
                        >
                            <Button
                                variant="npi-primary"
                                onClick={() => {
                                    setIsOpen(false);
                                }}
                                sx={{ width: '140px' }}
                            >
                                Close
                            </Button>
                        </Box>
                    </Box>
                </Formik>
            </ModalBox>
        </PageContent>
    );
};

export default ViewServicePage;
